import React, { useEffect } from "react";
import { Separator } from "../../ui/separator";

const Security = () => {

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="flex-grow bg-white py-8 md:py-12">
      <div className="container mx-auto px-2 md:px-4">
        <div className="max-w-4xl mx-auto">
          {/* Page Header */}
          <div className="mb-8 md:mb-12">
            <h1 className="text-3xl font-bold mb-6 text-center text-[#523880]">
              Oye Creators Security Policy
            </h1>
            <p className="text-sm text-gray-500 text-center mb-8">
              Last updated: Jun 02, 2025
            </p>
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            <section className="mb-8">
              <h2 className="text-xl font-semibold mt-8 mb-4 text-[#EE3788]">
                6. Data Classification & Lifecycle
              </h2>

              <div className="overflow-x-auto mb-6">
                <table className="min-w-full border-collapse border border-solid border-gray-300">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-solid border-gray-300 px-4 py-2 text-left">
                        Classification
                      </th>
                      <th className="border border-solid border-gray-300 px-4 py-2 text-left">
                        Examples
                      </th>
                      <th className="border border-solid border-gray-300 px-4 py-2 text-left">
                        Handling Rules
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-solid border-gray-300 px-4 py-2">
                        Public
                      </td>
                      <td className="border border-solid border-gray-300 px-4 py-2">
                        Marketing materials, public blog posts
                      </td>
                      <td className="border border-solid border-gray-300 px-4 py-2">
                        No special controls; published on public sites
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-solid border-gray-300 px-4 py-2">
                        Internal
                      </td>
                      <td className="border border-solid border-gray-300 px-4 py-2">
                        Internal memos, non-sensitive logs
                      </td>
                      <td className="border border-solid border-gray-300 px-4 py-2">
                        Access restricted to authenticated employees
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-solid border-gray-300 px-4 py-2">
                        Confidential
                      </td>
                      <td className="border border-solid border-gray-300 px-4 py-2">
                        Personal profiles, campaign briefs
                      </td>
                      <td className="border border-solid border-gray-300 px-4 py-2">
                        Encrypted at rest, strict RBAC, audit logging
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-solid border-gray-300 px-4 py-2">
                        Highly Confidential
                      </td>
                      <td className="border border-solid border-gray-300 px-4 py-2">
                        Payment details, government IDs
                      </td>
                      <td className="border border-solid border-gray-300 px-4 py-2">
                        AES-256 encryption, MFA for access, segregated backups
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="font-bold mb-2 text-base leading-relaxed text-gray-800">
                Data Lifecycle:
              </p>
              <ol className="list-decimal pl-5 mb-4 space-y-2">
                <li className="text-base leading-relaxed text-gray-800">
                  Collection: Only what's needed for matching, billing,
                  reporting.
                </li>
                <li className="text-base leading-relaxed text-gray-800">
                  Storage: Encrypted databases + hardened file servers.
                </li>
                <li className="text-base leading-relaxed text-gray-800">
                  Use: Access limited by "least privilege."
                </li>
                <li className="text-base leading-relaxed text-gray-800">
                  Archival: Moved to cold storage after 12 months, encrypted.
                </li>
                <li className="text-base leading-relaxed text-gray-800">
                  Deletion: Secure deletion (cryptographic wipe) upon request
                  or end-of-retention.
                </li>
              </ol>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4 text-[#EE3788]">
                7. Technical Controls
              </h2>

              <h3 className="text-lg font-semibold mb-2">
                7.1. Access Control:
              </h3>
              <ul className="list-disc pl-5 mb-4 space-y-2">
                <li>
                  RBAC: Roles defined for Brands, Creators, Admins, DevOps.
                </li>
                <li>MFA: Mandatory for all admin/dev accounts.</li>
                <li>
                  Password Policy: Minimum length 12, complexity, rotation
                  every 90 days.
                </li>
              </ul>

              <h3 className="text-lg font-semibold mb-2">
                7.2. Cryptography:
              </h3>
              <ul className="list-disc pl-5 mb-4 space-y-2">
                <li>In Transit: TLS 1.2+ for all web and API traffic.</li>
                <li>
                  At Rest: AES-256 for databases, file storage, backups.
                </li>
                <li>
                  Key Management: HSM for master keys; periodic rotation.
                </li>
              </ul>

              <h3 className="text-lg font-semibold mb-2">
                7.3. Network & Infrastructure:
              </h3>
              <ul className="list-disc pl-5 mb-4 space-y-2">
                <li>
                  Firewalls & Segmentation: Public DMZ for web servers;
                  private subnets for databases.
                </li>
                <li>IDS/IPS: 24×7 monitored for anomalies.</li>
                <li>
                  VPN & Bastion Hosts: Required for any remote admin access.
                </li>
              </ul>

              <h3 className="text-lg font-semibold mb-2">
                7.4. Endpoint & Application Security:
              </h3>
              <ul className="list-disc pl-5 mb-4 space-y-2">
                <li>
                  Secure Coding: OWASP Top 10 mitigations, code reviews,
                  static analysis.
                </li>
                <li>
                  Penetration Testing: Bi-annual by third-party specialists.
                </li>
                <li>
                  Patch Management: Critical patches applied within 72 hours.
                </li>
              </ul>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4 text-[#EE3788]">
                8. Organizational Measures
              </h2>
              <ul className="list-disc pl-5 mb-4 space-y-2">
                <li>
                  <strong>8.1. Policies & Procedures:</strong> Defines
                  acceptable use, change management.
                </li>
                <li>
                  <strong>8.2. Training & Awareness:</strong> Security
                  induction, quarterly phishing drills.
                </li>
                <li>
                  <strong>8.3. Incident Response:</strong> IR Plan with roles,
                  notifications.
                </li>
                <li>
                  <strong>8.4. Vendor Management:</strong> Due diligence, Data
                  Processing Agreements.
                </li>
              </ul>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4 text-[#EE3788]">
                9. Physical & Environmental Security
              </h2>
              <ul className="list-disc pl-5 mb-4 space-y-2">
                <li>
                  Data centers with 24×7 surveillance, biometric access
                  controls, fire suppression.
                </li>
                <li>
                  Office badge access, visitor logs, locked server rooms.
                </li>
              </ul>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4 text-[#EE3788]">
                10. Business Continuity & Disaster Recovery
              </h2>
              <ul className="list-disc pl-5 mb-4 space-y-2">
                <li>BCP/DR Plan: Annual exercises; RTO ≤ 4h, RPO ≤ 1h.</li>
                <li>Encrypted offsite backups; quarterly integrity tests.</li>
              </ul>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4 text-[#EE3788]">
                11. Data Retention & Secure Disposal
              </h2>
              <ul className="list-disc pl-5 mb-4 space-y-2">
                <li>Personal Profiles: 2 years post-deactivation.</li>
                <li>Financial Records: 7 years per Indian tax laws.</li>
                <li>Secure erase for disks; shredding for paper.</li>
              </ul>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4 text-[#EE3788]">
                12. Audit, Monitoring & Reporting
              </h2>
              <ul className="list-disc pl-5 mb-4 space-y-2">
                <li>Logging: All access, configuration changes.</li>
                <li>SIEM: Correlates logs, triggers alerts.</li>
                <li>
                  Monthly dashboards on patch status, incidents, audit
                  findings.
                </li>
              </ul>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4 text-[#EE3788]">
                13. Roles & Responsibilities
              </h2>
              <p className="mb-2">
                <strong>Brands & Creators:</strong>
              </p>
              <ul className="list-disc pl-5 mb-4 space-y-1">
                <li>Use strong, unique passwords.</li>
                <li>Report suspected account compromise.</li>
              </ul>

              <p className="mb-2">
                <strong>Platform Administrators:</strong>
              </p>
              <ul className="list-disc pl-5 mb-4 space-y-1">
                <li>Enforce policies, approve access requests.</li>
                <li>Lead incident response.</li>
              </ul>

              <p className="mb-2">
                <strong>Employees:</strong>
              </p>
              <ul className="list-disc pl-5 mb-4 space-y-1">
                <li>Follow security training.</li>
                <li>Handle data per classification rules.</li>
              </ul>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4 text-[#EE3788]">
                14. Indemnification & Liability
              </h2>
              <p className="mb-4">
                Brands and Creators agree to indemnify and hold harmless Oye
                Creators, its officers, and employees against any claims,
                damages or losses arising from:
              </p>
              <ul className="list-disc pl-5 mb-4 space-y-1">
                <li>Your negligence (e.g., sharing credentials).</li>
                <li>Misuse of the platform or breach of this Policy.</li>
              </ul>
              <p className="mb-4">
                Liability is capped per our Terms of Service, except where
                prohibited by law.
              </p>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4 text-[#EE3788]">
                15. Review & Updates
              </h2>
              <ul className="list-disc pl-5 mb-4 space-y-2">
                <li>
                  Policy Review: Annually or upon legal/technical changes.
                </li>
                <li>
                  Versioning: "Last Updated" date displayed on our website.
                </li>
              </ul>
              <p className="mt-6">
                <strong>Questions or Concerns?</strong> Contact{" "}
                <a
                  href="mailto:security@oyecreators.com"
                  className="text-oye-pink hover:underline"
                >
                  security@oyecreators.com
                </a>
                .
              </p>
            </section>
          </div>
        </div>
      </div>
    </main>
  )
}

export default Security
